import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchFolderContents,
  getBreadcrumb,
} from "@/features/files/services/sharepointService";
import type {
  SharepointFolder,
  SharepointFile,
  BreadcrumbItem,
} from "@/features/files/types/sharepoint";
import { Modal } from "@/shared/components/ui/Modal";
import { Button } from "@/shared/components/ui/Button";
import { Spinner } from "@/shared/components/ui/Spinner";
import { formatFileSize } from "@/shared/utils/fileHelpers";

interface FilesPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the selected files; parent performs the copy-attach. */
  onSelect: (files: SharepointFile[]) => Promise<void> | void;
}

const isVideo = (file: SharepointFile) =>
  (file.file_type || "").startsWith("video/");
const isImage = (file: SharepointFile) =>
  (file.file_type || "").startsWith("image/");

/**
 * Folder browser over the Files (SharePoint) feature so officers can attach
 * existing files — videos included — to an announcement without re-uploading.
 */
export const FilesPickerModal = ({
  isOpen,
  onClose,
  onSelect,
}: FilesPickerModalProps) => {
  const { t } = useTranslation();

  const [folderId, setFolderId] = useState<string | null>(null);
  const [folders, setFolders] = useState<SharepointFolder[]>([]);
  const [files, setFiles] = useState<SharepointFile[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [selected, setSelected] = useState<Map<string, SharepointFile>>(
    new Map(),
  );

  const loadFolder = useCallback(async (id: string | null) => {
    setLoading(true);
    try {
      const [contents, crumbs] = await Promise.all([
        fetchFolderContents(id),
        getBreadcrumb(id),
      ]);
      setFolders(contents.folders);
      setFiles(contents.files);
      setBreadcrumb(crumbs);
    } catch (err) {
      console.error("Failed to load folder:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset to root each time the picker opens
  useEffect(() => {
    if (!isOpen) return;
    setFolderId(null);
    setSelected(new Map());
    loadFolder(null);
  }, [isOpen, loadFolder]);

  const navigateTo = (id: string | null) => {
    setFolderId(id);
    loadFolder(id);
  };

  const toggleFile = (file: SharepointFile) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(file.id)) next.delete(file.id);
      else next.set(file.id, file);
      return next;
    });
  };

  const handleAttach = async () => {
    if (selected.size === 0) return;
    setAttaching(true);
    try {
      await onSelect([...selected.values()]);
      onClose();
    } catch (err) {
      console.error("Failed to attach files:", err);
    } finally {
      setAttaching(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("feed.form.choose_from_files", "Choose from Files")}
      size="lg"
      headerAction={
        <Button
          type="button"
          onClick={handleAttach}
          loading={attaching}
          disabled={selected.size === 0}
          className="text-sm py-1.5 px-3"
        >
          {t("feed.form.attach_selected", "Attach")}
          {selected.size > 0 ? ` (${selected.size})` : ""}
        </Button>
      }
    >
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-gray-500 mb-3 flex-wrap">
        {breadcrumb.map((crumb, i) => (
          <span key={crumb.id ?? "root"} className="flex items-center gap-1">
            {i > 0 && <span className="text-gray-300">/</span>}
            <button
              type="button"
              onClick={() => navigateTo(crumb.id)}
              disabled={crumb.id === folderId}
              className={
                crumb.id === folderId
                  ? "font-medium text-gray-800"
                  : "hover:text-indigo-600"
              }
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </nav>

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-1 max-h-[50vh] overflow-y-auto">
          {/* Folders */}
          {folders.map((folder) => (
            <button
              key={folder.id}
              type="button"
              onClick={() => navigateTo(folder.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg
                className="w-5 h-5 text-amber-400 shrink-0"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
              </svg>
              <span className="truncate">{folder.name}</span>
            </button>
          ))}

          {/* Files */}
          {files.map((file) => {
            const checked = selected.has(file.id);
            return (
              <button
                key={file.id}
                type="button"
                onClick={() => toggleFile(file)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors ${
                  checked
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span
                  className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${
                    checked
                      ? "bg-indigo-600 border-indigo-600 text-white"
                      : "border-gray-300"
                  }`}
                >
                  {checked && "✓"}
                </span>
                {isVideo(file) ? (
                  <svg
                    className="w-5 h-5 text-indigo-400 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                  </svg>
                ) : isImage(file) ? (
                  <svg
                    className="w-5 h-5 text-emerald-400 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 text-gray-400 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                )}
                <span className="flex-1 min-w-0 truncate">
                  {file.display_name}
                </span>
                {file.file_size != null && (
                  <span className="text-xs text-gray-400 shrink-0">
                    {formatFileSize(file.file_size)}
                  </span>
                )}
              </button>
            );
          })}

          {folders.length === 0 && files.length === 0 && (
            <p className="px-3 py-8 text-sm text-gray-400 italic text-center">
              {t("feed.form.folder_empty", "This folder is empty")}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
};
